import React from "react";
import cn from "./helpers/cn";

// Mapeamento de elevation MUI -> shadows Tailwind. AppBar default = elevation 4.
// (Idêntico ao mapeamento do Paper, já que AppBar é construído sobre Paper no MUI v4.)
const elevationMap: Record<number, string> = {
  0: "shadow-none",
  1: "shadow-sm",
  2: "shadow",
  3: "shadow-md",
  4: "shadow-lg",
  5: "shadow-xl",
  6: "shadow-2xl",
  ...Object.fromEntries(Array.from({ length: 18 }, (_, i) => [i + 7, "shadow-2xl"]))
};

// MUI v4 positionFixed também usa `@media print { position: absolute }` para que
// o AppBar não fique fixo na impressão.
const positionMap = {
  fixed: "fixed top-0 left-auto right-0 print:absolute",
  absolute: "absolute top-0 left-auto right-0",
  sticky: "sticky top-0 left-auto right-0",
  static: "static",
  relative: "relative",
} as const;

// Cores MUI v4:
//   default      bg = palette.background.default (#fafafa), texto contrast
//   primary      bg = palette.primary.main, texto = primary.contrastText
//   secondary    bg = palette.secondary.main, texto = secondary.contrastText
//   inherit      apenas color: inherit (bg permanece o de Paper = branco)
//   transparent  bg transparente, color: inherit (sombra continua válida)
const colorMap = {
  default: "bg-[#fafafa] text-slate-900",
  primary: "bg-primary text-contrast-text-primary",
  secondary: "bg-secondary text-contrast-text-secondary",
  inherit: "text-inherit",
  transparent: "bg-transparent text-inherit",
} as const;

type AppBarPosition = keyof typeof positionMap;
type AppBarColor = keyof typeof colorMap;

interface AppBarProps extends React.HTMLAttributes<HTMLElement> {
  component?: React.ElementType;
  color?: AppBarColor;
  position?: AppBarPosition;
  elevation?: number;
  variant?: "elevation" | "outlined";
  square?: boolean;
  children?: React.ReactNode;
}

const AppBar = React.forwardRef<HTMLElement, AppBarProps>((props, ref) => {
  const {
    className,
    component: Component = "header",
    color = "primary",
    position = "fixed",
    elevation = 4,
    variant = "elevation",
    square = true,
    ...other
  } = props;

  const classes = cn(
    // Base do MUI v4 (root): flex coluna, largura total, box-border, sem encolher,
    // z-index do AppBar = 1100. Inclui bg-white para que `colorInherit` tenha
    // o background herdado de Paper, como faz o MUI.
    "flex flex-col w-full box-border shrink-0 z-[1100] bg-white",

    positionMap[position],
    colorMap[color],

    // Paper.square é default true em AppBar.
    !square && "rounded",

    // Variantes do Paper (AppBar herda):
    //   outlined: borda + sem sombra
    //   elevation: shadow conforme `elevation` (MUI mantém a sombra mesmo com
    //              color="transparent" — o usuário zera passando elevation={0}).
    variant === "outlined" && "border border-slate-200 shadow-none",
    variant === "elevation" && elevationMap[elevation],

    className
  );

  return <Component ref={ref} className={classes} {...other} />;
});

AppBar.displayName = "AppBar";

export default AppBar;
